/** Shared rms-api connection infrastructure: JWT lifecycle, path resolution,
 *  HTTP-service selection (direct vs Laravel proxy), and authenticated request
 *  methods. Used by any service that calls rms-api endpoints. */
import { injectable } from "inversify";
import { rootContainer, TYPE } from "./container/root";
import { IApiResponse, IHttpService } from "./http-service";

interface ITokenResponse {
    token: string;
    expires_in: number;
}

@injectable()
class RmsApiConnectionManager {
    private readonly laravelHttpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);
    private readonly rmsApiHttpService: IHttpService = rootContainer.get<IHttpService>(TYPE.RmsApiHttp);
    private jwt: string = '';
    private tokenExpirationTime: number = 0;
    private readonly _isDirectConnection: boolean = !this.rmsApiHttpService.getBaseUrl()?.includes('7072');

    /**
     *  Ensures a valid JWT is cached and set as Authorization header.
     *  Fetches a new token from Laravel when missing or within 5 s of expiry.
     */
    private async ensureValidToken(): Promise<void> {
        const now = Date.now();
        if (!this.jwt || !this.tokenExpirationTime || now >= this.tokenExpirationTime) {
            const res = await this.laravelHttpService.get<IApiResponse<ITokenResponse>>(
                '/rms-api/rms-api-token'
            );
            this.jwt = res.payload.token;
            this.tokenExpirationTime = now + (res.payload.expires_in * 1000) - 5000;

            const targetHttpService = this._isDirectConnection ? this.rmsApiHttpService : this.laravelHttpService;
            targetHttpService.setCustomHeaders({
                'Authorization': `Bearer ${this.jwt}`
            });
        }
    }

    private resolvePath(endpoint: string): string {
        return this._isDirectConnection ? endpoint : `/rms-api-js${endpoint}`;
    }

    private getTargetHttpService(): IHttpService {
        return this._isDirectConnection ? this.rmsApiHttpService : this.laravelHttpService;
    }

    /** Authenticated GET to rms-api. Handles token refresh and path resolution internally. */
    public async get<T>(path: string, queryParams?: Record<string, any>): Promise<T> {
        await this.ensureValidToken();
        return this.getTargetHttpService().get<T>(this.resolvePath(path), queryParams);
    }

    /** Authenticated POST to rms-api. */
    public async post<T>(path: string, data?: any): Promise<T> {
        await this.ensureValidToken();
        return this.getTargetHttpService().post<T>(this.resolvePath(path), data);
    }

    /** Authenticated PATCH to rms-api. */
    public async patch<T>(path: string, data?: any): Promise<T> {
        await this.ensureValidToken();
        return this.getTargetHttpService().patch<T>(this.resolvePath(path), data);
    }

    /** Authenticated PUT to rms-api. */
    public async put<T>(path: string, data?: any): Promise<T> {
        await this.ensureValidToken();
        return this.getTargetHttpService().put<T>(this.resolvePath(path), data);
    }

    /** Authenticated DELETE to rms-api. */
    public async remove<T>(path: string, data?: any): Promise<T> {
        await this.ensureValidToken();
        return this.getTargetHttpService().remove<T>(this.resolvePath(path), data);
    }
}

export default RmsApiConnectionManager;
